/**
 * syncQueue.ts — Offline mutation queue.
 *
 * When the app is offline, POST/PATCH/DELETE requests are stored
 * in IndexedDB.  On reconnect, they are replayed in FIFO order.
 *
 * Each entry carries an idempotencyKey (UUID) so the server can
 * deduplicate replayed mutations — preventing double-prescriptions,
 * duplicate invoices, etc.
 */

import { v4 as uuidv4 } from 'uuid'
import { getDb, withTimeout } from './offlineDb'

const MAX_RETRIES = 5
const MAX_QUEUE_SIZE = 1000

export interface SyncEntry {
  url: string
  method: string
  body: string | null
  timestamp: number
  retries: number
  idempotencyKey: string
}

/**
 * Add a failed mutation to the offline sync queue.
 */
export async function addToSyncQueue(
  entry: Omit<SyncEntry, 'timestamp' | 'retries' | 'idempotencyKey'>,
  idempotencyKey?: string,
): Promise<void> {
  try {
    const db = await withTimeout(getDb())

    // Bound the queue to prevent unbounded growth
    const count = await withTimeout(db.count('syncQueue'))
    if (count >= MAX_QUEUE_SIZE) {
      console.warn('Sync queue full — discarding oldest entry')
      const tx = db.transaction('syncQueue', 'readwrite')
      const cursor = await tx.store.openCursor()
      if (cursor) await cursor.delete()
      await tx.done
    }

    await withTimeout(db.add('syncQueue', {
      ...entry,
      timestamp: Date.now(),
      retries: 0,
      idempotencyKey: idempotencyKey || uuidv4(),
    }))
  } catch (error) {
    console.warn('Failed to add to sync queue:', error)
    // Silently fail - queue is best-effort
  }
}

/**
 * Get the number of pending sync entries.
 */
export async function getSyncQueueCount(): Promise<number> {
  try {
    const db = await withTimeout(getDb())
    return await withTimeout(db.count('syncQueue'))
  } catch (error) {
    console.warn('Failed to get sync queue count:', error)
    return 0
  }
}

/**
 * Process all queued mutations in FIFO order.
 * Returns the number of successfully synced entries.
 */
export async function processSyncQueue(): Promise<number> {
  try {
    const db = await withTimeout(getDb())
    const tx = db.transaction('syncQueue', 'readonly')
    const keys = await withTimeout(tx.store.getAllKeys())
    await tx.done

    let synced = 0

    for (const key of keys) {
      const entry = await db.get('syncQueue', key)
      if (!entry) continue

    try {
      const csrfMatch = document.cookie.match(/csrftoken=([^;]+)/)
      const csrfToken = csrfMatch ? csrfMatch[1] : ''

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken
      }
      // Idempotency key prevents duplicate record creation on replay
      if (entry.idempotencyKey) {
        headers['Idempotency-Key'] = entry.idempotencyKey
      }

      const response = await fetch(entry.url, {
        method: entry.method,
        headers,
        body: entry.body,
        credentials: 'include',
      })

      if (response.ok || response.status === 409) {
        // Success or conflict (skip — server version wins)
        await db.delete('syncQueue', key)
        synced++
      } else if (response.status >= 500) {
        // Server error — retry later
        if (entry.retries >= MAX_RETRIES) {
          await db.delete('syncQueue', key)
        } else {
          await db.put('syncQueue', { ...entry, retries: entry.retries + 1 }, key)
        }
      } else {
        // Client error (4xx except 409) — discard
        await db.delete('syncQueue', key)
        synced++
      }
    } catch {
      // Still offline or network error — stop processing
      break
    }
  }

    return synced
  } catch (error) {
    console.warn('Failed to process sync queue:', error)
    return 0
  }
}
